import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Loader2, ParkingCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { visitsApi, type WeeklyVisitItem, type OwnershipFilter } from '@/api/visits';
import { getClientLocations, type ClientLocation } from '@/api/client-locations';
import { getVisitPriority, getPriorityClasses, type VisitPriorityParams } from '@/lib/priority';
import type { Waypoint, WaypointType } from '../lib/types';
import { cn } from '@/lib/utils';

interface ClientChecklistProps {
  onAddWaypoints: (waypoints: Omit<Waypoint, 'id'>[]) => void;
  existingWaypointClientIds: Set<number>;
}

interface LocationWithClient {
  client: {
    id: number;
    name: string;
  };
  location: ClientLocation;
  priority: string;
  isVisitedOrSkipped: boolean;
}

// LocalStorage key for ownership filter (must match VisitsPage.tsx)
const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function getStoredOwnershipFilter(): OwnershipFilter {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return ['own'];
}

export function ClientChecklist({ onAddWaypoints, existingWaypointClientIds }: ClientChecklistProps) {
  const ownershipFilter = useMemo(() => getStoredOwnershipFilter(), []);

  // Load weekly visits
  const { data: visitsData, isLoading: visitsLoading } = useQuery({
    queryKey: ['route-planner-visits', ownershipFilter],
    queryFn: () => visitsApi.getWeeklyVisits(undefined, ownershipFilter),
    staleTime: 60000,
  });

  // Calculate priority for visit items
  const getItemPriority = useCallback((item: WeeklyVisitItem) => {
    const params: VisitPriorityParams = {
      isActive: item.visit?.status === 'ACTIVE',
      isUrgent: item.visit?.priority === 'URGENT',
      hasDeadline: item.deadlineStatus === 'overdue' || item.deadlineStatus === 'today',
      isPreferredDayToday: item.isPreferredDayToday,
      hasPendingVisit: item.visit?.status === 'PENDING',
      isVisited: item.visit?.status === 'VISITED',
      isSkipped: item.visit?.status === 'SKIPPED',
      hasOutdoorTask: false,
    };
    return getVisitPriority(params);
  }, []);

  // Sort visits by priority
  const sortedVisits = useMemo(() => {
    if (!visitsData?.visits) return [];

    return [...visitsData.visits].sort((a, b) => {
      const priorityA = getItemPriority(a);
      const priorityB = getItemPriority(b);

      const priorityOrder = ['entered', 'urgent', 'deadline', 'today', 'visit', 'regular', 'been'];
      const orderA = priorityOrder.indexOf(priorityA);
      const orderB = priorityOrder.indexOf(priorityB);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.client.name.localeCompare(b.client.name, 'ru');
    });
  }, [visitsData, getItemPriority]);

  // Stable query key - join IDs as string
  const clientIdsKey = useMemo(
    () => sortedVisits.map((v) => v.client.id).join(','),
    [sortedVisits]
  );

  // Load all locations for all clients (parallel)
  const { data: allLocations, isLoading: locationsLoading } = useQuery({
    queryKey: ['route-planner-all-locations', clientIdsKey],
    queryFn: async () => {
      // Load all locations in parallel
      const results = await Promise.all(
        sortedVisits.map(async (item) => {
          try {
            const locations = await getClientLocations(item.client.id);
            return { item, locations: locations.filter((l) => l.type === 'PARKING') };
          } catch {
            return { item, locations: [] };
          }
        })
      );

      // Flatten into LocationWithClient array
      const result: LocationWithClient[] = [];
      for (const { item, locations } of results) {
        for (const location of locations) {
          result.push({
            client: { id: item.client.id, name: item.client.name },
            location,
            priority: getItemPriority(item),
            isVisitedOrSkipped: item.visit?.status === 'VISITED' || item.visit?.status === 'SKIPPED',
          });
        }
      }

      return result;
    },
    enabled: sortedVisits.length > 0 && clientIdsKey.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Add single location directly to route
  const handleAddLocation = useCallback(
    (item: LocationWithClient) => {
      const type: WaypointType = 'P';
      const officeSuffix = item.location.office ? ` (${item.location.office.name})` : '';

      onAddWaypoints([
        {
          lat: item.location.latitude,
          lon: item.location.longitude,
          label: `${item.client.name}${officeSuffix} - ${item.location.name}`,
          type,
          clientId: item.client.id,
          officeId: item.location.officeId,
        },
      ]);
    },
    [onAddWaypoints]
  );

  // Add all available locations
  const handleAddAll = useCallback(() => {
    if (!allLocations) return;

    const waypoints: Omit<Waypoint, 'id'>[] = [];

    for (const item of allLocations) {
      if (item.isVisitedOrSkipped) continue;
      if (existingWaypointClientIds.has(item.client.id)) continue;

      const type: WaypointType = 'P';
      const officeSuffix = item.location.office ? ` (${item.location.office.name})` : '';

      waypoints.push({
        lat: item.location.latitude,
        lon: item.location.longitude,
        label: `${item.client.name}${officeSuffix} - ${item.location.name}`,
        type,
        clientId: item.client.id,
        officeId: item.location.officeId,
      });
    }

    if (waypoints.length > 0) {
      onAddWaypoints(waypoints);
    }
  }, [allLocations, existingWaypointClientIds, onAddWaypoints]);

  const isLoading = visitsLoading || locationsLoading;

  // Count available locations (not in route, not visited) - must be before early returns
  const availableCount = useMemo(() => {
    if (!allLocations) return 0;
    return allLocations.filter(
      (item) => !item.isVisitedOrSkipped && !existingWaypointClientIds.has(item.client.id)
    ).length;
  }, [allLocations, existingWaypointClientIds]);

  if (visitsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!visitsData?.visits || visitsData.visits.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">Нет клиентов на эту неделю</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with Add All */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          Доступно: {availableCount}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleAddAll}
          disabled={isLoading || availableCount === 0}
          className="text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Добавить все
        </Button>
      </div>

      {/* Flat location list */}
      <div className="space-y-1 max-h-[400px] overflow-y-auto">
        {locationsLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-2" />
            <span className="text-sm text-muted-foreground">Загрузка точек...</span>
          </div>
        )}

        {allLocations && allLocations.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">Нет парковок у клиентов</p>
          </div>
        )}

        {allLocations?.map((item) => {
          const key = `${item.client.id}-${item.location.id}`;
          const isAlreadyInRoute = existingWaypointClientIds.has(item.client.id);

          return (
            <button
              key={key}
              onClick={() => !isAlreadyInRoute && handleAddLocation(item)}
              disabled={isAlreadyInRoute}
              className={cn(
                'w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors',
                getPriorityClasses(item.priority as Parameters<typeof getPriorityClasses>[0]),
                isAlreadyInRoute && 'opacity-50 cursor-not-allowed',
                !isAlreadyInRoute && 'hover:opacity-80 active:scale-[0.98]'
              )}
            >
              {/* Add icon */}
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                  isAlreadyInRoute
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-primary/10 text-primary'
                )}
              >
                <Plus className="w-4 h-4" />
              </div>

              {/* Parking icon */}
              <ParkingCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />

              {/* Client name - Location name */}
              <div className="flex-1 min-w-0">
                <div
                  className={cn(
                    'text-sm truncate',
                    item.isVisitedOrSkipped && 'line-through'
                  )}
                >
                  <span className="font-medium">{item.client.name}</span>
                  <span className="text-muted-foreground"> — {item.location.name}</span>
                </div>
                {item.location.office && (
                  <div className="text-xs text-muted-foreground truncate">
                    {item.location.office.name}
                  </div>
                )}
                {isAlreadyInRoute && (
                  <div className="text-xs text-muted-foreground">Уже в маршруте</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
